import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import MediaCarousel from './components/MediaCarousel';
import ArtistsCarousel from './components/ArtistsCarousel';
import type { Artist } from './components/ArtistCard';
import SongCard, { Song } from './components/SongCard';
import { Carousel3DHandle } from './components/Carousel3D';
import { ChevronLeft } from 'lucide-react';
import CarouselArrows from './components/CarouselArrows';
import VinylOverlay from './components/VinylOverlay';
import { Button } from './components/ui/button';

interface ArtistData extends Artist {
  songs: Song[];
}

const artists: ArtistData[] = (() => {
  const artistModules = import.meta.glob('../../../levels/*/artist.json', {
    eager: true,
  });
  const levelModules = import.meta.glob('../../../levels/*/*/level.json', {
    eager: true,
  });

  const map: Record<string, ArtistData> = {};

  for (const [path, mod] of Object.entries(artistModules)) {
    const parts = path.split('/');
    const artistId = parts[parts.length - 2];
    const data = (
      (mod as { default?: Record<string, unknown> }).default ?? mod
    ) as Record<string, unknown>;
    map[artistId] = {
      id: data.id as string,
      name: data.name as string,
      image: `/levels/${artistId}/${(data.image as string)}`,
      songs: [],
    };
  }

  for (const [path, mod] of Object.entries(levelModules)) {
    const parts = path.split('/');
    const artistId = parts[parts.length - 3];
    const levelId = parts[parts.length - 2];
    const data = (
      (mod as { default?: Record<string, unknown> }).default ?? mod
    ) as Record<string, unknown>;
    const title = (
      (data.charts as Array<{ song?: { title?: string } }> | undefined)?.[0]
        ?.song?.title ?? levelId
    ) as string;
    const artist = map[artistId];
    if (artist) {
      artist.songs.push({
        id: levelId,
        name: title as string,
        image: `/levels/${artistId}/placeholder.svg`,
      });
    }
  }

  return Object.values(map);
})();

export default function App() {
  const historyState = (window.history.state ?? {}) as {
    selectedId?: string;
    artistIndex?: number;
    view?: 'artists' | 'songs';
  };
  const [selected, setSelected] = useState<ArtistData | null>(() => {
    if (historyState.selectedId) {
      return (
        artists.find((a) => a.id === historyState.selectedId) ?? null
      );
    }
    return null;
  });
  const [view, setView] = useState<'artists' | 'songs'>(
    () => historyState.view ?? 'artists',
  );
  const [transition, setTransition] =
    useState<'toSongs' | 'toArtists' | null>(null);
  const [artistIndex, setArtistIndex] = useState(
    () => historyState.artistIndex ?? 0,
  );
  const prefersReducedMotion = useReducedMotion();
  const artistCarouselRef = useRef<Carousel3DHandle>(null);
  const songCarouselRef = useRef<Carousel3DHandle>(null);
  const [artistCarouselReady, setArtistCarouselReady] = useState(true);
  const [songCarouselReady, setSongCarouselReady] = useState(false);
  const activeCarouselRef =
    transition === 'toSongs'
      ? songCarouselRef
      : transition === 'toArtists'
        ? artistCarouselRef
        : view === 'songs'
          ? songCarouselRef
          : artistCarouselRef;
  const activeCarouselReady =
    transition === 'toSongs'
      ? songCarouselReady
      : transition === 'toArtists'
        ? artistCarouselReady
        : view === 'songs'
          ? songCarouselReady
          : artistCarouselReady;
  const [overlay, setOverlay] = useState({
    slideHeight: 0,
    carouselRadius: 0,
    tintColor: '#ff4fd8',
    tintOpacity: 0,
  });

  useEffect(() => {
    if (artists.length > 0) {
      const preload = new Image();
      preload.src = artists[0].image;
    }
  }, []);

  const handleArtistSelect = (artist: ArtistData): void => {
    const index = artists.findIndex((a) => a.id === artist.id);
    setArtistIndex(index);
    setSelected(artist);
    setSongCarouselReady(false);
    setTransition('toSongs');
    console.log('handleArtistSelect', {
      artistIndex: index,
      selectedId: artist.id,
      transition: 'toSongs',
    });
  };

  const handleBack = (): void => {
    setArtistCarouselReady(false);
    setTransition('toArtists');
    console.log('handleBack', {
      artistIndex,
      selectedId: selected?.id,
      transition: 'toArtists',
    });
  };

  const handleArtistCarouselInit = useCallback(
    (): void => {
      const target = artistIndex;
      console.log('handleArtistCarouselInit: Embla API initialized', { target });
      const off = artistCarouselRef.current?.onSettle(() => {
        const current = artistCarouselRef.current?.selectedIndex;
        console.log('handleArtistCarouselInit: settle event', { current, target });
        if (current === target) {
          setArtistCarouselReady(true);
          off?.();
        }
      });
      artistCarouselRef.current?.scrollTo(target);
    },
    [artistIndex]
  );

  const handleSongCarouselInit = useCallback((): void => {
    console.log('handleSongCarouselInit: Embla API initialized');
    const off = songCarouselRef.current?.onSettle(() => {
      console.log('handleSongCarouselInit: settle event');
      setSongCarouselReady(true);
      off?.();
    });
    const current = songCarouselRef.current?.selectedIndex;
    songCarouselRef.current?.scrollTo(0);
    if (current === 0) {
      setSongCarouselReady(true);
      off?.();
    }
  }, []);

  const overlayBackground =
    view === 'songs' || transition === 'toSongs'
      ? 'radial-gradient(circle at center, #444 0%, #111 60%)'
      : 'radial-gradient(circle at center, #111 0%, #000 60%)';

  useEffect(() => {
    window.history.replaceState(
      { ...(window.history.state ?? {}), selectedId: selected?.id ?? null, artistIndex, view },
      '',
    );
  }, [selected, artistIndex, view]);

  const handleSongPlay = (song: Song): void => {
    if (!selected) return;
    window.history.replaceState(
      { ...(window.history.state ?? {}), selectedId: selected.id, artistIndex, view },
      '',
    );
    const path = `/play/${encodeURIComponent(selected.id)}/${encodeURIComponent(song.id)}`;
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  useEffect(() => {
    if (transition === 'toArtists') {
      const carousel = artistCarouselRef.current;
      console.log('toArtists effect', {
        carouselPresent: !!carousel,
        carouselReady: carousel?.isReady,
        artistIndex,
      });
      if (!carousel || !carousel.isReady) {
        console.log('toArtists effect: carousel not ready, skipping scrollTo');
        // Embla hasn't initialized yet; handleArtistCarouselInit will handle scrolling
        // once the carousel is ready.
        return;
      }
      const off = carousel.onSettle(() => {
        console.log('toArtists effect: settle event');
        setArtistCarouselReady(true);
        off();
      });
      console.log('toArtists effect: executing scrollTo', { artistIndex });
      carousel.scrollTo(artistIndex);
    }
  }, [transition, artistIndex]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-ddr">
      {/* Font logo */}
      <div className="absolute left-4 top-4 font-display text-2xl italic tracking-wider text-accent drop-shadow-neon">
        BeatsStage
      </div>
      <div className="absolute right-4 top-4">
        <Button asChild>
          <a href="/maker">Level Maker</a>
        </Button>
      </div>

      {/* Artists / Songs */}
      <div className="relative isolate min-h-screen">
        <AnimatePresence initial={false}>
          {(view === 'artists' || transition === 'toArtists') && (
            <motion.div
              key="artists"
              className="absolute inset-0 flex items-center justify-center"
              style={{
                pointerEvents:
                  view === 'artists' && transition === null && artistCarouselReady
                    ? 'auto'
                    : 'none',
              }}
              initial={{
                opacity: transition === 'toArtists' ? 0 : 1,
                scale: transition === 'toArtists' ? 1.15 : 1,
              }}
              animate={
                transition === 'toSongs'
                  ? { opacity: 0, scale: 1.15 }
                  : artistCarouselReady
                    ? { opacity: 1, scale: 1 }
                    : { opacity: 0, scale: 1.15 }
              }
              transition={{ duration: 0.8, ease: 'easeInOut' }}
              onAnimationComplete={() => {
                if (transition === 'toSongs') {
                  setView('songs');
                  setTransition(null);
                }
              }}
            >
              <ArtistsCarousel
                ref={artistCarouselRef}
                artists={artists}
                selectedId={selected?.id}
                initialIndex={artistIndex}
                onInit={handleArtistCarouselInit}
                onSelect={handleArtistSelect}
                vinylTintColor="#ff4fd8"
                vinylTintOpacity={transition === 'toSongs' ? 1 : 0}
                onOverlayChange={setOverlay}
              />
            </motion.div>
          )}
          {selected && (view === 'songs' || transition === 'toSongs') && (
            <motion.div
              key="songs"
              className="absolute inset-0 flex items-center justify-center"
              style={{
                pointerEvents:
                  view === 'songs' && transition === null && songCarouselReady
                    ? 'auto'
                    : 'none',
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: transition === 'toArtists' ? 0 : 1,
                scale: transition === 'toArtists' ? 0.8 : 1,
              }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
              onAnimationComplete={() => {
                if (transition === 'toArtists') {
                  setView('artists');
                  setTransition(null);
                }
              }}
            >
              <MediaCarousel<Song>
                forceMobileLayout
                ref={songCarouselRef}
                items={selected.songs}
                initialIndex={0}
                onInit={handleSongCarouselInit}
                onBack={handleBack}
                hideBackButton
                vinylTintColor="#ff4fd8"
                vinylTintOpacity={transition === 'toArtists' ? 0 : 1}
                onOverlayChange={setOverlay}
                tiltEnabled={false}
                renderItem={(item, index, selectedIndex, ctx) => {
                  const isSelected = index === selectedIndex;
                  const glow = isSelected
                    ? 'drop-shadow(0 0 12px rgba(255,255,255,0.8))'
                    : 'drop-shadow(0 0 0 rgba(0,0,0,0))';
                  return (
                    <motion.div
                      key={item.id}
                      animate={{ filter: glow }}
                      transition={{ duration: 0.075, ease: 'easeInOut' }}
                    >
                      <SongCard
                        ref={ctx.ref}
                        song={item}
                        tiltEnabled={ctx.tiltEnabled}
                        onPlay={handleSongPlay}
                      />
                    </motion.div>
                  );
                }}
              />
              <div className="absolute left-1/2 top-4 z-20 w-full max-w-md -translate-x-1/2 rounded-full border border-hairline bg-black/50 px-4 py-2 text-white backdrop-blur-md">
                <div className="relative flex items-center">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm"
                    onClick={handleBack}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Back</span>
                  </button>
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-center">
                    {selected.name}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <VinylOverlay
          slideHeight={overlay.slideHeight}
          carouselRadius={overlay.carouselRadius}
          background={overlayBackground}
          tintColor={overlay.tintColor}
          tintOpacity={overlay.tintOpacity}
        />
      </div>

      <CarouselArrows
        onPrev={() => activeCarouselRef.current?.scrollPrev()}
        onNext={() => activeCarouselRef.current?.scrollNext()}
        disabled={transition !== null || !activeCarouselReady}
      />

      {/* Zoom-through overlay from/to the selected artist card */}
      <AnimatePresence>
        {!prefersReducedMotion && selected && transition && (
          <motion.img
            key={`zoom-${selected.id}-${transition}`}
            src={selected.image}
            layoutId={`artist-card-${selected.id}`}
            className="pointer-events-none fixed left-1/2 top-1/2 z-[5] h-[320px] w-[180px] -translate-x-1/2 -translate-y-1/2 rounded-3xl object-cover"
            initial={
              transition === 'toSongs'
                ? { opacity: 1, scale: 1, filter: 'blur(0px)' }
                : { opacity: 1, scale: 1.4, filter: 'blur(2px)' }
            }
            animate={
              transition === 'toSongs'
                ? { opacity: 0, scale: 1.4, filter: 'blur(2px)' }
                : { opacity: 0, scale: 1, filter: 'blur(0px)' }
            }
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
          />
        )}
      </AnimatePresence>

    </div>
  );
}
